import { useMutation, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../../hooks/useApiHelper";
import { CustomerSettingQueryKeys } from "./keys";
import { CreateUpdateCustomerSetting } from "../../models/CustomerSettings/CreateUpdateCustomerSetting";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useNotification from "../../components/shared/Notifications/useNotifications";

export default function useCustomerSettingsMutations(customerId: string) {
  const queryClient = useQueryClient();
  const { saveHandlers } = useDefaultCRUDHandlers("Customer Settings");
  const { addNotification } = useNotification();
  const { put, post } = useApiHelper();

  const create = useMutation({
    mutationFn: (newSetting: CreateUpdateCustomerSetting) =>
      post(`/customer/${customerId}/settings`, newSetting),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: CustomerSettingQueryKeys.all(customerId),
      });
      saveHandlers.onSuccess();
    },
    onError: () => {
      saveHandlers.onError();
    },
  });

  // Adds several settings in one go. The API only takes one setting per call,
  // so we post them in sequence and report on the batch as a whole.
  const createMany = useMutation({
    mutationFn: async (newSettings: CreateUpdateCustomerSetting[]) => {
      const failedKeys: string[] = [];

      for (const newSetting of newSettings) {
        try {
          await post(`/customer/${customerId}/settings`, newSetting);
        } catch {
          failedKeys.push(newSetting.key);
        }
      }

      if (failedKeys.length > 0) {
        throw new Error(`Failed to add: ${failedKeys.join(", ")}`);
      }

      return newSettings.length;
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: CustomerSettingQueryKeys.all(customerId),
      });
    },
    onSuccess: (count) => {
      addNotification({
        variant: "success",
        primaryText: `${count} setting${count === 1 ? "" : "s"} added`,
      });
    },
    onError: (error: Error) => {
      addNotification({
        variant: "error",
        primaryText: "Failed to save customer settings",
        secondaryText: error.message,
      });
    },
  });

  const update = useMutation({
    mutationFn: (updatedSetting: CreateUpdateCustomerSetting) =>
      put(`/customer/${customerId}/settings/${updatedSetting.id}`, updatedSetting),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: CustomerSettingQueryKeys.all(customerId),
      });
      saveHandlers.onSuccess();
    },
    onError: () => {
      saveHandlers.onError();
    },
  });

  return {
    create,
    createMany,
    update,
  };
}
